import React from "react";
//#region types
import { DialogContentHandles, DialogProps } from "models/propTypes/dialogPropTypes";
import { DialogEnums, StyleEnums } from "models/enums";
import { IconDefinitionConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { DateRange } from "models/formTypes/util";
//#endregion
//#region components
import { StaticInfoMessage, useMountedState } from "components/common";
import { Grid } from "@mui/material";
import { Label, TextAreaInput } from "components/common/wintForms";
import MonthInput from "components/common/wintForms/formFields/monthInput";
import unconnectedFormField from "components/common/wintForms/unconnectedFormField";
import { useMonthlyReportService } from "serviceHooks";
import { useDebouncedCallback } from "use-debounce";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { isReportableRange, toGenerateRequest, toMonthLimits, withStart } from "./reportPeriod";

/**
 * The dialogs of the advisor review flow, and the company instruction that steers it.
 *
 * Each body keeps its own value and writes it into a draft object the dialog's action reads on
 * submit. That is lighter than a `useWintForm` container for one field. Apart from the generate
 * dialog's month count, these dialogs validate nothing beyond "is it filled in" — the endpoints do
 * the real validation and answer with a message the shared error interceptor surfaces.
 *
 * Each body stacks its children through a column Grid: the dialog renders `children` straight
 * into its layout, so anything dropped in as bare siblings sits flush against the next element.
 */

/**
 * The generate dialog's body.
 *
 * It is a component rather than a bare Grid because the dialog clones its single child with a `ref`
 * and `onFormValidationChange` (see `dialogs/_layout.tsx`), and that callback is the only route by
 * which the footer learns whether its action may run. Without it the Generate action stays enabled
 * with no valid period picked and rejects into an `onError` these dialogs never define: a click that
 * does nothing whatsoever. `zeroGrossSalaryChangeContent` reports validity the same way.
 *
 * From and To can span any number of months, so the one-or-three rule is checked here and shown in
 * the message slot in place of the description: two stacked messages would push the error below
 * the text the advisor has already read.
 */
const GenerateMonthlyReportBody = React.forwardRef<
  HTMLDivElement,
  { onChange: (range?: DateRange) => void; onFormValidationChange?: (isValid: boolean) => void }
>(({ onChange, onFormValidationChange }, ref) => {
  const [range, setRange] = React.useState<DateRange>({});
  // Reports are for completed months: a month still being booked would read as a weak month.
  const maxDate = React.useMemo(() => DateHelper.getLastDayOfPreviousMonth(new Date()), []);
  const endLimits = toMonthLimits(range.start, maxDate);

  const isValid = isReportableRange(range);
  const showError = !!range.start && !!range.end && !isValid;

  const update = (next: DateRange) => {
    setRange(next);
    onChange(next);
  };

  React.useEffect(() => {
    onFormValidationChange?.(isValid);
  }, [isValid, onFormValidationChange]);

  return (
    <Grid container direction={"column"} spacing={3} ref={ref}>
      <Grid item>
        <Label>{LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.MonthlyReportPeriod)}</Label>
        <Grid container direction="row" spacing={1}>
          <Grid item xs={6}>
            <MonthInput
              {...unconnectedFormField}
              name="periodStart"
              label={TranslationKeys.Common.From}
              value={range.start}
              setValue={(start?: Date) => update(withStart(range, start))}
              maxDate={maxDate}
              hasError={showError}
              // Laid out inside the dialog body the month grid is clipped by its overflow.
              popperPositionFixed
            />
          </Grid>
          <Grid item xs={6}>
            <MonthInput
              {...unconnectedFormField}
              name="periodEnd"
              label={TranslationKeys.Common.To}
              value={range.end}
              setValue={(end?: Date) => update({ ...range, end })}
              minDate={endLimits.minDate}
              maxDate={endLimits.maxDate}
              excludeMonths={endLimits.excludeMonths}
              setLastDayOfMonth
              hasError={showError}
              popperPositionFixed
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        {showError ? (
          <StaticInfoMessage displayType={StyleEnums.DisplayType.Danger}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.MonthlyReportPeriodInvalid)}
          </StaticInfoMessage>
        ) : (
          <StaticInfoMessage displayType={StyleEnums.DisplayType.Info}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.GenerateMonthlyReportDescription)}
          </StaticInfoMessage>
        )}
      </Grid>
    </Grid>
  );
});

/**
 * `label` arrives already localized: the `BaseInput` family behind `TextAreaInput` renders
 * `props.label` raw, so handing it a translation key puts the key itself on screen.
 */
const TextField: React.FC<{
  name: string;
  label: string;
  rows: number;
  maxLength?: number;
  initialValue?: string;
  onChange: (v: string) => void;
}> = ({ name, label, rows, maxLength, initialValue, onChange }) => {
  const [value, setValue] = React.useState<string>(initialValue ?? "");

  return (
    <TextAreaInput
      {...unconnectedFormField}
      name={name}
      label={label}
      rows={rows}
      maxLength={maxLength}
      value={value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
        onChange(e.target.value);
      }}
    />
  );
};

/**
 * Generation returns as soon as the row exists, in `Generating` — the pass itself takes 15-90
 * seconds in the report service's background. `onGenerated` gets the new id so the caller can
 * navigate to the report and poll there rather than hold the dialog open.
 */
const useGenerateMonthlyReportDialog = (onGenerated: (id: string) => void) => {
  const { generateReport } = useMonthlyReportService();

  const getDialog = (): DialogProps => {
    const draft: { range?: DateRange } = {};

    return {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.GenerateMonthlyReport),
      titleIcon: IconDefinitionConstants.WReport1,
      dialogType: DialogEnums.DialogType.Content,
      maxWidth: "sm",
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.Generate),
          disableWhenInvalid: true,
          onClick: async () => {
            // Unreachable while the line above stands. Kept as the type guard, and it throws rather
            // than resolving so a regression cannot quietly submit nothing.
            if (!isReportableRange(draft.range)) throw new Error("Generate submitted without a valid period");

            const summary = await generateReport(toGenerateRequest(draft.range));
            if (summary.id) onGenerated(summary.id);
            return summary;
          },
        },
      ],
      children: <GenerateMonthlyReportBody onChange={(range) => (draft.range = range)} />,
    };
  };

  return getDialog;
};

/**
 * Unlike generate, this endpoint waits: it runs the whole 15-90 second pass before answering, and
 * never moves the report into `Generating` while it does. So there is no server state to poll —
 * the dialog hands the in-flight promise to `onStarted` and closes immediately, and the report
 * page shows the wait against the report the advisor is changing rather than behind a modal.
 */
const useRegenerateMonthlyReportDialog = (reportId: string, onStarted: (call: Promise<unknown>) => void) => {
  const { regenerateReport } = useMonthlyReportService();

  const getDialog = (): DialogProps => {
    const draft: { instruction: string } = { instruction: "" };

    return {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.RegenerateMonthlyReport),
      titleIcon: IconDefinitionConstants.WRepeat,
      dialogType: DialogEnums.DialogType.Content,
      maxWidth: "sm",
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.Regenerate),
          onClick: () => {
            onStarted(regenerateReport(reportId, { kaFeedback: draft.instruction, discardPriorMarkdown: false }));
            // Resolving now is what closes the dialog; the page owns the rest of the wait.
            return Promise.resolve();
          },
        },
      ],
      children: (
        <Grid container direction={"column"} spacing={3}>
          <Grid item>
            <TextField
              name="instruction"
              label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.RegenerateInstruction)}
              rows={4}
              onChange={(v) => (draft.instruction = v)}
            />
          </Grid>
          <Grid item>
            <StaticInfoMessage displayType={StyleEnums.DisplayType.Info}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.RegenerateDescription)}
            </StaticInfoMessage>
          </Grid>
        </Grid>
      ),
    };
  };

  return getDialog;
};

const useEditMonthlyReportDialog = (reportId: string, currentMarkdown?: string) => {
  const { editReportMarkdown } = useMonthlyReportService();

  const getDialog = (onDone?: () => void): DialogProps => {
    const draft: { markdown: string } = { markdown: currentMarkdown ?? "" };

    return {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.EditMonthlyReport),
      titleIcon: IconDefinitionConstants.WEdit,
      dialogType: DialogEnums.DialogType.Content,
      // md rather than sm: a markdown body is a multi-row editor, and dialogs.md ties that width
      // to content shape rather than to the nearest dialog it was copied from.
      maxWidth: "md",
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Save),
          onClick: () =>
            editReportMarkdown(reportId, draft.markdown).then((report) => {
              onDone && onDone();
              return report;
            }),
        },
      ],
      children: (
        <TextField
          name="markdown"
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.MonthlyReportMarkdown)}
          rows={16}
          initialValue={currentMarkdown}
          onChange={(v) => (draft.markdown = v)}
        />
      ),
    };
  };

  return getDialog;
};

/**
 * Approval is the one irreversible-in-practice step: it publishes a `CreateCustomerCaseMessage`
 * and Core notifies the customer, and unpublishing afterwards un-notifies nobody. The dialog
 * therefore states the consequence rather than asking "are you sure", and the warning carries
 * what the advisor can't undo.
 */
const useApproveMonthlyReportDialog = (reportId: string) => {
  const { approveReport } = useMonthlyReportService();

  const getDialog = (onDone?: () => void): DialogProps => ({
    isOpen: false,
    title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.ApproveMonthlyReportTitle),
    dialogType: DialogEnums.DialogType.Message,
    type: StyleEnums.DisplayType.Success,
    maxWidth: "sm",
    actions: [
      {
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.ApproveAndShare),
        type: StyleEnums.DisplayType.Success,
        successMessage: TranslationKeys.Advisor.MonthlyReportApproved,
        onClick: () =>
          approveReport(reportId).then((report: WintSuperkollAPIModel.MonthlyReport) => {
            onDone && onDone();
            return report;
          }),
      },
    ],
    children: (
      <Grid container direction={"column"} spacing={3}>
        <Grid item>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.ApproveMonthlyReportDescription)}
        </Grid>
        <Grid item>
          <StaticInfoMessage displayType={StyleEnums.DisplayType.Warning}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.ApproveMonthlyReportWarning)}
          </StaticInfoMessage>
        </Grid>
      </Grid>
    ),
  });

  return getDialog;
};

const useUnpublishMonthlyReportDialog = (reportId: string) => {
  const { unpublishReport } = useMonthlyReportService();

  const getDialog = (onDone?: () => void): DialogProps => ({
    isOpen: false,
    title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.UnpublishMonthlyReportTitle),
    dialogType: DialogEnums.DialogType.Message,
    maxWidth: "sm",
    actions: [
      {
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.UnpublishMonthlyReport),
        successMessage: TranslationKeys.Advisor.MonthlyReportUnpublished,
        onClick: () =>
          unpublishReport(reportId).then((report) => {
            onDone && onDone();
            return report;
          }),
      },
    ],
    children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.UnpublishMonthlyReportDescription),
  });

  return getDialog;
};

// Long enough that a pause mid-sentence does not save every few words, short enough that closing
// the tab right after typing rarely loses anything. Closing the dialog flushes regardless.
const COMPANY_INSTRUCTION_SAVE_DELAY_MS = 800;

// The report service's limit. Enforced here too so the advisor cannot type past what will be saved.
const COMPANY_INSTRUCTION_MAX_LENGTH = 4000;

type SaveState = "unchanged" | "saving" | "saved" | "failed";

const saveStateMessage: Record<SaveState, string> = {
  unchanged: TranslationKeys.Advisor.CompanyInstructionDescription,
  saving: TranslationKeys.Advisor.CompanyInstructionSaving,
  saved: TranslationKeys.Advisor.CompanyInstructionSaved,
  failed: TranslationKeys.Advisor.CompanyInstructionSaveFailed,
};

/**
 * Saves as the advisor types, so there is no Save button to forget: the instruction applies to
 * every later report, and one lost on close would silently change the next month's report.
 *
 * A forwardRef because the dialog calls `onClose` on its child's ref when it closes, which is where
 * a save still waiting out its delay is sent rather than dropped.
 */
const CompanyInstructionBody = React.forwardRef<DialogContentHandles>((_props, ref) => {
  const { getCompanyInstruction, saveCompanyInstruction } = useMonthlyReportService();
  const { getMounted } = useMountedState();

  const [initialText, setInitialText] = React.useState<string>();
  const [loadFailed, setLoadFailed] = React.useState<boolean>(false);
  const [saveState, setSaveState] = React.useState<SaveState>("unchanged");

  React.useEffect(() => {
    getCompanyInstruction()
      .then((instruction) => getMounted() && setInitialText(instruction.text ?? ""))
      .catch(() => getMounted() && setLoadFailed(true));
    // Deps are empty: the service hook's functions are fresh each render, so including them would loop.
  }, []);

  // Saves run one at a time: two PUTs in flight can commit out of order and leave the older text
  // stored with nothing in the UI to show it. Only the newest save may set the status, so an
  // earlier one finishing cannot show "Sparat" while a later one is still on its way.
  const saveQueue = React.useRef<Promise<void>>(Promise.resolve());
  const latestSaveId = React.useRef(0);

  const save = useDebouncedCallback((text: string) => {
    const saveId = ++latestSaveId.current;
    setSaveState("saving");
    const setStateIfLatest = (state: SaveState) => {
      if (getMounted() && saveId === latestSaveId.current) setSaveState(state);
    };
    saveQueue.current = saveQueue.current
      .then(() => saveCompanyInstruction(text))
      .then(() => setStateIfLatest("saved"))
      .catch(() => setStateIfLatest("failed"));
  }, COMPANY_INSTRUCTION_SAVE_DELAY_MS);

  React.useImperativeHandle(ref, () => ({ onClose: () => save.flush() }), [save]);

  if (loadFailed) {
    return (
      <StaticInfoMessage displayType={StyleEnums.DisplayType.Danger}>
        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.CompanyInstructionLoadFailed)}
      </StaticInfoMessage>
    );
  }

  return (
    <Grid container direction={"column"} spacing={3}>
      <Grid item>
        {initialText === undefined ? (
          // The field mounts only once the stored text is known: TextField seeds its value on mount,
          // and an advisor typing into an empty field before the load lands would have it overwritten.
          <TextAreaInput
            {...unconnectedFormField}
            name="companyInstruction"
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.CompanyInstructionLabel)}
            rows={8}
            value=""
            disabled
            isLoading
          />
        ) : (
          <TextField
            name="companyInstruction"
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.CompanyInstructionLabel)}
            rows={8}
            maxLength={COMPANY_INSTRUCTION_MAX_LENGTH}
            initialValue={initialText}
            onChange={save}
          />
        )}
      </Grid>
      <Grid item>
        <StaticInfoMessage
          displayType={saveState === "failed" ? StyleEnums.DisplayType.Danger : StyleEnums.DisplayType.Info}
        >
          {LocalizationHelperInstance.getFormattedMessage(saveStateMessage[saveState])}
        </StaticInfoMessage>
      </Grid>
    </Grid>
  );
});

/**
 * The advisor's standing instruction for the company — focus, interpretation and tone — which the
 * report service applies to every generation and regeneration from then on.
 */
const useCompanyInstructionDialog = () => {
  const getDialog = (): DialogProps => ({
    isOpen: false,
    title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.CompanyInstruction),
    titleIcon: IconDefinitionConstants.WSettings,
    dialogType: DialogEnums.DialogType.Content,
    maxWidth: "sm",
    // No actions: the text saves as it is typed, so the footer only needs a way out.
    cancelButtonText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Close),
    children: <CompanyInstructionBody />,
  });

  return getDialog;
};

export {
  useGenerateMonthlyReportDialog,
  useRegenerateMonthlyReportDialog,
  useEditMonthlyReportDialog,
  useApproveMonthlyReportDialog,
  useUnpublishMonthlyReportDialog,
  useCompanyInstructionDialog,
};
